import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import PictionJS from 'piction-js';
import classNames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import UMOGridItemOverlayMenu from './UMOGridItemOverlayMenu';
import { SearchParams, SearchTag, AlternatesTag } from '../../utils/searchUtils';
import { selectToggle as workspaceSelectToggle } from '../../actions/workspacePreview';
import { selectToggle as umoGallerySelectToggle, retrieveUMOs } from '../../actions/umoGallery';
import { toggleUMODetailView, toggleWorkspaceDetailView, showMainDialog } from '../../actions/mainUI';
import { removeFromWorkspace } from '../../actions/umo';
import { moveUmoHandler } from '../../utils/umoUtils';
import { reportImage } from '../../utils/uiUtils';
import appConfig from '../../appConfig';
import { MODAL_VIEW_TYPE_SHARE } from '../../constants/mainContentTypes';

function UMOGridItemOverlay({
  umo,
  onDetailViewClick,
  toggleFullImageView,
  isNotJustified,
  isWorkspaceItem,
  highlightSetID,
  isSmallGrid,
  isUMOGalleryWorkspace,
  isSelected,
  overlayClassModifier,
  dispatch,
  selectedWorkspaceData = { id: null },
  intl,
  isUnseenUpdate,
  galleryType,
  isRestricted,
}) {
  // eslint-disable-next-line no-underscore-dangle
  const hasAlternates = umo._data.re > 1;
  const zoomifyURL = umo.data && umo.data.z && umo.data.z[0];
  const onSelect = (e) => {
    // todo: refactor. correct select toggle should probably be given
    if (isWorkspaceItem) {
      dispatch(workspaceSelectToggle(umo.id));
    } else {
      dispatch(umoGallerySelectToggle(umo.id));
    }
    e.stopPropagation();
  };

  const retrieveAlternateUMOs = (umoID) => {
    dispatch(retrieveUMOs(new SearchParams({
      tagList: [new AlternatesTag(
         umoID,
         'Alternate Images',
         umoID,
        <FormattedMessage id="umoGridItem.alternateItemTagLabel" />,
        <FormattedMessage id="umoGridItem.alternateItemTagDescription" />
       )],
    })
    ));
  };

  const onAlternatesClick = (e) => {
    // todo: refactor. correct select toggle should probably be given
    retrieveAlternateUMOs(umo.id);
    e.stopPropagation();
  };

  const onViewDetailClick = (item) => {
    if (!item.shiftKey || !item.ctrlKey) {
      if (isUMOGalleryWorkspace && !appConfig.removeWorkspaceCardView) {
        dispatch(toggleWorkspaceDetailView(umo.id, selectedWorkspaceData));
      } else if (onDetailViewClick) {
        onDetailViewClick(umo.id);
      } else {
        dispatch(toggleUMODetailView(umo.id));
      }
    }
  };

  const onWorkspaceAddClick = (item) => {
    if (!item.shiftKey || !item.ctrlKey) {
      if (selectedWorkspaceData.id) {
        const origin = {
          id: 1,
          isWorkspace: false,
        };
        const target = {
          id: selectedWorkspaceData.id,
          isWorkspace: true,
        };
        moveUmoHandler(origin, target, [umo.id], dispatch);
      }
    }
    item.stopPropagation();
  };

  const onWorkspaceRemoveClick = (e) => {
    if (highlightSetID || selectedWorkspaceData.id) {
      dispatch(removeFromWorkspace(highlightSetID || selectedWorkspaceData.id, [umo]));
    }
    e.stopPropagation();
  };

  const retrieveRelatedUMOs = (umoID) => {
    dispatch(retrieveUMOs(new SearchParams({
      tagList: [new SearchTag(
        'search',
        'RELATED_IMAGES',
        umoID,
        <FormattedMessage id="umoGridItem.relatedItemTagLabel" />,
        <FormattedMessage id="umoGridItem.relatedItemTagDescription" />,
      )],
    })
    ));
  };

  const onReportImage = (e) => {
    e.stopPropagation();
    reportImage(dispatch, umo.id, umo.name, intl.locale);
  };

  const showFullImage = (e) => {
    e.stopPropagation();
    const umoData = {
      fullSrc: umo.type === 'PHOTO' ? umo.webQuality.url : umo.originalList.all[0]._data.$.h,
      type: umo.type,
      zoomifyURL,
    };
    toggleFullImageView(true, umoData);
  };

  const workspaceAddTooltip = (
    <Tooltip id="workspace-add-tooltip">
      {selectedWorkspaceData.id ?
        intl.formatMessage({ id: 'umoGridItem.addToWorkspaceTooltip' }, { workspaceName: selectedWorkspaceData.name })
        :
        intl.formatMessage({ id: 'umoGridItem.selectWorkspaceTooltip' })}
    </Tooltip>
  );

  const reportImageTooltip = (
    <Tooltip id="workspace-add-tooltip">
      {intl.formatMessage({ id: 'umoGridItem.reportImageTooltip' })}
    </Tooltip>
  );

  const addToWorkspaceButton = appConfig.removeWorkspaces ? null :
    (<OverlayTrigger placement="bottom" overlay={workspaceAddTooltip}>
      <button
        onClick={onWorkspaceAddClick}
        className={classNames('umo-grid-item__overlay-more-add', isSmallGrid ? 'sm-overlay' : '')}
      >
        <i className="umo-grid-item__overlay-more-add-icon" />
      </button>
    </OverlayTrigger>);
  const getShareLinks = (e) => {
    e.stopPropagation();
    dispatch(showMainDialog(MODAL_VIEW_TYPE_SHARE, { umoID: umo.id }));
  };
  const shareLinkBtn = (
    <button
      id="share-button"
      className={classNames('umo-grid-item__overlay-share-image', appConfig.reportImage ? '' : 'no-report', isSmallGrid ? 'sm-overlay' : '')}
      onClick={getShareLinks}
    >
      <i className="umo-grid-item__overlay-share-image-button" />
    </button>);
  // Allow fullscreen view for photos (web quality) or documents/records (html view)
  const fullImageType = (umo.type === 'PHOTO' && umo.webQuality)
    || (!['PHOTO', 'AUDIO', 'VIDEO'].includes(umo.type) && umo.originalList.all[0] &&
      umo.originalList.all[0]._data.$.h);

  const zoomifyBtn = !isRestricted && fullImageType ? (
    <button
      id="zoomify-button"
      onClick={showFullImage}
      className={classNames('umo-grid-item__overlay-fullscreen', isSmallGrid ? 'sm-overlay' : '')}
    >
      <i className="umo-grid-item__overlay-fullscreen-button" />
    </ button>
  ) : null;

  return (
    <div
      className={classNames('umo-grid-item__overlay', overlayClassModifier, {
        'umo-grid-item__overlay--noj': isNotJustified,
        'umo-grid-item__overlay--sm': isSmallGrid,
      })}
    >
      {isUnseenUpdate ? <span className={classNames('umo-grid-item__overlay-updated', { noj: isNotJustified, sm: isSmallGrid })} /> : null}
      <div onClick={onViewDetailClick} className="umo-grid-item__overlay-tools">
        {
         !hasAlternates ? '' :
         <button onClick={onAlternatesClick} className={classNames('umo-grid-item__overlay-alternates', isSmallGrid ? 'sm-overlay' : '')}>
           <span
             className={classNames(isSelected ?
                'umo-grid-item__overlay-alternates--checked' : 'umo-grid-item__overlay-alternates-icon'
              )}
           >D</span>
         </button>
        }

        <button onClick={onSelect} className={classNames('umo-grid-item__overlay-select', isSmallGrid ? 'sm-overlay' : '')}>
          <i
            className={classNames(isSelected ?
              'umo-grid-item__overlay-select-icon--checked' : 'umo-grid-item__overlay-select-icon'
            )}
          />
        </button>
        {
          galleryType === SearchParams.searchTypes.WORKSPACE || isWorkspaceItem ||
          isUMOGalleryWorkspace || galleryType === SearchParams.searchTypes.HIGHLIGHT ?
            <button onClick={onWorkspaceRemoveClick} className={classNames('umo-grid-item__overlay-more-remove', isSmallGrid ? 'sm-overlay' : '')}>
              <i className="umo-grid-item__overlay-more-remove-icon" />
            </button>
            :
            addToWorkspaceButton
        }

        {
          isWorkspaceItem ? '' :
          <div className={classNames('umo-grid-item__overlay-more-images', isSmallGrid ? 'sm-overlay' : '')}>
            <UMOGridItemOverlayMenu
              umo={umo}
              retrieveRelatedUMOs={retrieveRelatedUMOs}
              classModifier={isSmallGrid ? 'sm-overlay' : ''}
              isRestricted={isRestricted}
            />
          </div>
        }
        { appConfig.reportImage ?
          <OverlayTrigger placement="bottom" overlay={reportImageTooltip}>
            <button
              id="report-button"
              className={classNames('umo-grid-item__overlay-report-image', isSmallGrid ? 'sm-overlay' : '')}
              onClick={onReportImage}
            >
              <i className="umo-grid-item__overlay-report-image-button" />
            </button>
          </OverlayTrigger> : null
        }
        {appConfig.enableLinkShare && !isRestricted ? shareLinkBtn : null}
        {zoomifyBtn}
      </div>
    </div>
  );
}

UMOGridItemOverlay.propTypes = {
  onDetailViewClick: PropTypes.func,
  toggleFullImageView: PropTypes.func,
  umo: PropTypes.instanceOf(PictionJS.UMO),
  isNotJustified: PropTypes.bool,
  isWorkspaceItem: PropTypes.bool,
  highlightSetID: PropTypes.string,
  isSmallGrid: PropTypes.bool,
  isUMOGalleryWorkspace: PropTypes.bool,
  isSelected: PropTypes.bool,
  overlayClassModifier: PropTypes.string,
  dispatch: PropTypes.func,
  selectedWorkspaceData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  isUnseenUpdate: PropTypes.bool,
  galleryType: PropTypes.string,
  isRestricted: PropTypes.bool,
};

export default injectIntl(UMOGridItemOverlay);



// WEBPACK FOOTER //
// ./src/js/components/UMOGridItem/UMOGridItemOverlay.jsx